import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Snackbar } from '@mui/material';
import { getSecuritySettings } from '../../utils/securityService';

/**
 * Idle sign-out (Nov 2026, multi-tenant security). Signs staff out of the portal after the
 * district's idle limit (default 30 minutes). Activity in any open portal tab counts (shared via
 * localStorage). A warning appears one minute before.
 */
const KEY = 'lastActivityAt';
const DEFAULT_MINUTES = 30;
const WARN_MS = 60 * 1000;

const now = () => Date.now();
const readLast = () => { try { return Number(localStorage.getItem(KEY)) || now(); } catch (e) { return now(); } };
const touch = () => { try { localStorage.setItem(KEY, String(now())); } catch (e) { /* ignore */ } };

const IdleSignOut = () => {
  const navigate = useNavigate();
  const [limitMs, setLimitMs] = useState(DEFAULT_MINUTES * 60 * 1000);
  const [warning, setWarning] = useState(false);
  const lastWrite = useRef(0);

  useEffect(() => {
    (async () => {
      const res = await getSecuritySettings();
      if (res.ok && Number(res.portalIdleMinutes) > 0) setLimitMs(Number(res.portalIdleMinutes) * 60 * 1000);
    })();
  }, []);

  useEffect(() => {
    // Don't reset an existing timestamp: reopening the portal after hours away must still sign out.
    try { if (!localStorage.getItem(KEY)) touch(); } catch (e) { /* ignore */ }
    const onActivity = () => {
      if (now() - lastWrite.current > 5000) { lastWrite.current = now(); touch(); setWarning(false); }
    };
    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'wheel'];
    events.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    const timer = setInterval(() => {
      const idle = now() - readLast();
      if (idle >= limitMs) {
        try { sessionStorage.setItem('signedOutReason', `You were signed out after ${Math.round(limitMs / 60000)} minutes without activity.`); } catch (e) { /* ignore */ }
        navigate('/auth/logout');
      } else {
        setWarning(idle >= limitMs - WARN_MS);
      }
    }, 15000);
    return () => {
      events.forEach((e) => window.removeEventListener(e, onActivity));
      clearInterval(timer);
    };
  }, [limitMs, navigate]);

  return (
    <Snackbar open={warning} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
      <Alert severity="warning" action={<Button color="inherit" size="small" onClick={() => { touch(); setWarning(false); }}>Stay signed in</Button>}>
        You'll be signed out in about a minute because of inactivity.
      </Alert>
    </Snackbar>
  );
};

export default IdleSignOut;
